if (typeof debounce === 'undefined') {
  var debounce = function(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  };
}
/**
 *  @class
 *  @function ThemeHeader
 */
if (!customElements.get('theme-header')) {
  class ThemeHeader extends HTMLElement {
    connectedCallback() {
      this.header_section = document.querySelector('.header-section');
      this.menu = this.querySelector('#mobile-menu');
      this.toggle = document.querySelector('.mobile-toggle-wrapper');
      this.toggleButton = this.toggle.querySelector('.mobile-toggle');
      // Every window/document listener is added with this signal and removed in disconnectedCallback, so a
      // header the theme editor replaces leaves nothing running behind it.
      this._listeners = new AbortController();
      const signal = this._listeners.signal;
      // Last value written per :root property: a write only happens when the measurement changed.
      this._rootValues = {};

      // The drawer must live outside the header: .header-section creates a
      // stacking context (sticky, z-index 50) that sits below the
      // click-capture (52), so a fixed drawer left inside it could never
      // paint above the dim. It is rendered inside the header section for
      // access to the section's settings/blocks, then teleported to
      // #wrapper next to the other side panels. Stale copies from previous
      // section renders (theme editor) are removed first.
      const wrapper = document.getElementById('wrapper');
      if (this.menu && wrapper) {
        document.querySelectorAll('.mobile-menu-drawer').forEach((el) => {
          if (el !== this.menu) el.remove();
        });
        wrapper.appendChild(this.menu);
      }
      this._scrollTicking = false;
      this._stickyOffset = 0;

      // The drawer opens through OverlayManager, which provides the
      // click-capture, scroll lock and focus trap (Escape closes it in app.js).
      // The hamburger state is synced from the panel events so every
      // close path (capture click, Escape, close button) resets it.
      this.toggleButton.addEventListener('click', (e) => {
        e.preventDefault();
        if (OverlayManager.isOpen(this.menu)) {
          OverlayManager.close(this.menu);
        } else {
          OverlayManager.open(this.menu);
        }
        window.dispatchEvent(new Event('resize.resize-select'));
      }, { signal });
      document.addEventListener('panel:open', (e) => {
        if (e.detail.panel === this.menu) {
          this.toggle.classList.add('active');
          this.toggleButton.setAttribute('aria-expanded', 'true');
          // The drawer has no close button on phones, so OverlayManager leaves focus on the toggle: move it
          // into the dialog, so screen readers start inside the menu.
          if (!this.menu.contains(document.activeElement)) {
            this.menu.focus({ preventScroll: true });
          }
          // The drawer sits under the header, so the header comes back with it.
          this.setHidden(false);
          // Measure the header again as the menu opens. Its position only
          // matters while the drawer is on screen, and anything that shifted
          // the layout since load (the announcement bar marquee collapsing,
          // late fonts, a dismissed bar) would otherwise leave the drawer
          // sitting in a gap below the header.
          this.setHeaderBottom();
        } else if (OverlayManager.isOpen(this.menu)) {
          // Another panel opened from the header (search, the bag) takes the menu's place. Both sit on the same layer
          // and the menu comes later in the page, so left open it would cover the new panel below the header.
          // Focus is already in the new panel, so closing the menu does not move it.
          OverlayManager.close(this.menu);
        }
      }, { signal });
      document.addEventListener('panel:closed', (e) => {
        if (e.detail.panel === this.menu) {
          this.toggle.classList.remove('active');
          this.toggle.removeAttribute('open');
          this.toggleButton.setAttribute('aria-expanded', 'false');
        }
      }, { signal });

      // Cache sticky offset and recalculate on resize
      this._cacheStickyOffset();
      window.addEventListener('resize', debounce(() => {
        this.setPinnedHeight();
        this._cacheStickyOffset();
        this.setHeaderBottom();
        this.setAnnouncementHeight();
      }, 100), { signal });

      // Single scroll listener with rAF throttle
      window.addEventListener('scroll', () => {
        if (!this._scrollTicking) {
          this._scrollTicking = true;
          requestAnimationFrame(() => {
            this.setStickyClass();
            this.setHiddenOnScroll();
            this.setHeaderOffset();
            this.setHeaderHeight();
            this.setHeaderBottom();
            this._scrollTicking = false;
          });
        }
      }, { passive: true, signal });

      window.dispatchEvent(new Event('scroll'));

      // Once the header finishes sliding away or back, re-measure its bottom edge so
      // anything pinned under it (e.g. a sticky filter bar) lands in the right place.
      // Only the header's own transition: the menu, icons and links inside it bubble theirs up here too.
      this.header_section.addEventListener('transitionend', (event) => {
        if (event.target === this.header_section) this.setHeaderBottom();
      }, { signal });

      // Those vars are otherwise only refreshed on scroll, so they keep
      // whatever the layout was at load. The announcement bar marquee
      // collapses shortly after load (and late fonts can resize the header),
      // which left them measuring a taller header than the one on screen —
      // the mobile menu then opened a gap below it. Re-measure whenever the
      // header or the bar actually changes geometry. The bar is observed as
      // well because its collapse moves the header without resizing it.
      if ('ResizeObserver' in window) {
        this._headerResizeObserver = new ResizeObserver(() => {
          this.setPinnedHeight();
          this.setHeaderOffset();
          this.setHeaderHeight();
          this.setHeaderBottom();
          this.setAnnouncementHeight();
        });
        this._headerResizeObserver.observe(this);
        const a_bar_section = document.querySelector('.announcement-bar-section');
        if (a_bar_section) {
          this._headerResizeObserver.observe(a_bar_section);
        }
      }

      // Set from the start (and kept current by the observer and resize above), not on the first scroll, so the
      // sections sized with it do not jump when the shopper starts scrolling.
      this.setAnnouncementHeight();
      this.setPinnedHeight();
      if (document.querySelector('.announcement-bar-section')) {
        window.dispatchEvent(new Event('resize'));
      }

      // Buttons.
      this.menu.querySelectorAll('summary').forEach(summary => summary.addEventListener('click', this.onSummaryClick.bind(this)));
      this.menu.querySelectorAll('.parent-link-back--button').forEach(button => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
    }

    _cacheStickyOffset() {
      if (this.classList.contains('header-sticky--active')) {
        // Skip recalculation while sticky — the header is position:sticky at top:0,
        // so getBoundingClientRect().top would return 0 and corrupt the offset.
        if (this.classList.contains('is-sticky')) {
          return;
        }
        this._stickyOffset = parseInt(this.header_section.getBoundingClientRect().top, 10) + document.documentElement.scrollTop;
      }
    }
    setStickyClass() {
      if (this.classList.contains('header-sticky--active')) {
        this.classList.toggle('is-sticky', window.scrollY >= this._stickyOffset && window.scrollY > 0);
      }
    }
    // Slides the header away while the shopper scrolls down and brings it back on any scroll up.
    // It stays put while the menu is open or keyboard focus is inside it, so it never hides what is in use.
    setHiddenOnScroll() {
      if (!this.classList.contains('header--hide-on-scroll')) {
        return;
      }
      // Direction is judged from where the last change of direction happened, not frame to frame, so the
      // few pixels of jitter in a fast or momentum scroll cannot flip the header back and forth.
      const threshold = 12;
      const y = window.scrollY;
      if (this._scrollAnchor === undefined) this._scrollAnchor = y;
      // :focus-visible, not any focus: a drawer that closes returns focus to its opener in the header, and after a
      // tap that focus is not visible, so the header keeps sliding away; after a keyboard close it is, and it stays.
      const inUse = this.header_section.querySelector(':focus-visible') || this.menu.classList.contains('active');
      const hidden = this.header_section.classList.contains('header-section--hidden');
      if (inUse || y <= this.header_section.offsetHeight) {
        this._scrollAnchor = y;
        this.setHidden(false);
        return;
      }
      // Keep the anchor at the turning point: the lowest point while hidden, the highest while shown.
      if (hidden ? y > this._scrollAnchor : y < this._scrollAnchor) {
        this._scrollAnchor = y;
      } else if (Math.abs(y - this._scrollAnchor) >= threshold) {
        this._scrollAnchor = y;
        this.setHidden(!hidden);
      }
    }
    // The header and everything pinned under it (filter bar, tabs, sticky sidebars) read this one state,
    // so they start moving on the same frame. Both copies are checked: the theme editor replaces the section wrapper.
    setHidden(hidden) {
      const root = document.documentElement;
      if (this.header_section.classList.contains('header-section--hidden') === hidden && root.hasAttribute('data-header-hidden') === hidden) return;
      this.header_section.classList.toggle('header-section--hidden', hidden);
      root.toggleAttribute('data-header-hidden', hidden);
    }
    // A :root custom property invalidates style for the whole page, so it is written only when it changed.
    setRootValue(name, value) {
      if (this._rootValues[name] === value) return;
      this._rootValues[name] = value;
      document.documentElement.style.setProperty(name, value);
    }
    // By the section's class: in the header group its wrapper id carries the group's id, not a fixed one.
    setAnnouncementHeight() {
      const a_bar = document.querySelector('.announcement-bar-section');
      if (a_bar) {
        this.setRootValue('--announcement-height', a_bar.clientHeight + 'px');
      }
    }
    disconnectedCallback() {
      // The theme editor re-renders the section — without this the old
      // listeners and observer keep firing against a detached header.
      this._listeners?.abort();
      // A re-rendered header starts shown: the bars pinned under it must not stay at the top.
      document.documentElement.removeAttribute('data-header-hidden');
      if (this._headerResizeObserver) {
        this._headerResizeObserver.disconnect();
        this._headerResizeObserver = null;
      }
    }
    // Where the header's bottom edge is once it is pinned at the top: its own height (sticky at top 0), or 0 for a header
    // that scrolls away. Unlike --header-bottom it does not change while the announcement bar scrolls out, so the bars
    // pinned under the header (which transition their top) only move when the header itself slides away or back.
    setPinnedHeight() {
      const pinned = getComputedStyle(this.header_section).position === 'sticky';
      this.setRootValue('--header-pinned-height', (pinned ? this.header_section.getBoundingClientRect().height : 0) + 'px');
    }
    setHeaderOffset() {
      this.setRootValue('--header-offset', this.header_section.getBoundingClientRect().top + 'px');
    }
    setHeaderHeight() {
      this.setRootValue('--header-height', this.clientHeight + 'px');
    }
    // The header's real bottom edge. --header-offset + --header-height only
    // approximates it: clientHeight drops the border, and neither accounts
    // for a transparent header being taken out of flow. Measure it directly
    // so the mobile menu can sit flush under the header. Clamped at 0 so a
    // non-sticky header scrolling away puts the menu at the viewport top.
    // Only a header at rest is measured. Slid away it reads 0 and mid-slide it reads in between, while the bars
    // pinned under it are heading for its resting edge: keep that value, and measure again once it settles.
    setHeaderBottom() {
      if (this.header_section.classList.contains('header-section--hidden') || this.header_section.getAnimations().length) {
        return;
      }
      this.setRootValue('--header-bottom', Math.max(0, this.getBoundingClientRect().bottom) + 'px');
    }
    onSummaryClick(event) {
      const summaryElement = event.currentTarget;
      const detailsElement = summaryElement.parentNode;
      const parentLinksEnabled = this.menu.dataset.parentLinks === 'true';

      // When parent links setting is on, only arrow opens submenu
      if (parentLinksEnabled) {
        event.preventDefault();
        const isArrowClick = event.target.closest('.link-forward');

        if (!isArrowClick) {
          const anchor = summaryElement.querySelector('a');
          if (anchor && anchor.href) {
            window.location.href = anchor.href;
          }
          return;
        }
        // Arrow clicked — manually open details
        detailsElement.setAttribute('open', '');
      }

      const parentMenuElement = detailsElement.closest('.link-container');

      // Stamp deferred template content on first open
      const tpl = detailsElement.querySelector(':scope > template.mobile-menu-deferred');
      if (tpl) {
        tpl.replaceWith(tpl.content.cloneNode(true));
        // Re-bind back buttons for the newly stamped content
        detailsElement.querySelectorAll('.parent-link-back--button').forEach(button => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
        detailsElement.querySelectorAll('details.link-container summary').forEach(summary => summary.addEventListener('click', this.onSummaryClick.bind(this)));
      }

      // The drawer was moved out of the header (connectedCallback), so the submenu is looked up in the drawer.
      if (this.menu.querySelector('.parent-link-back--button')) {
        this.menu.scrollTop = 0;
      }

      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
      }, 100);
    }
    onCloseButtonClick(event) {
      event.preventDefault();
      const detailsElement = event.currentTarget.closest('details');
      this.closeSubmenu(detailsElement);
    }
    closeSubmenu(detailsElement) {
      detailsElement.classList.remove('menu-opening');
      this.closeAnimation(detailsElement);
    }
    closeAnimation(detailsElement) {
      let animationStart;

      const handleAnimation = (time) => {
        if (animationStart === undefined) {
          animationStart = time;
        }

        const elapsedTime = time - animationStart;

        if (elapsedTime < 400) {
          window.requestAnimationFrame(handleAnimation);
        } else {
          detailsElement.removeAttribute('open');
        }
      };

      window.requestAnimationFrame(handleAnimation);
    }
  }
  customElements.define('theme-header', ThemeHeader);
}
/**
 *  @class
 *  @function FullMenu
 */
if (!customElements.get('full-menu')) {
  class FullMenu extends HTMLElement {
    connectedCallback() {
      // removed when the theme editor replaces the header
      this._listeners = new AbortController();
      this.setupToggles();

      this.submenus = this.querySelectorAll('.thb-full-menu>.menu-item-has-children:not(.menu-item-has-megamenu)>.sub-menu');

      if (!this.submenus.length) {
        return;
      }
      // resize on initial load
      window.addEventListener('resize', debounce(() => {
        this.resizeSubMenus();
      }, 100), { signal: this._listeners.signal });

      window.dispatchEvent(new Event('resize'));

      document.fonts.ready.then(() => {
        this.resizeSubMenus();
      });
    }
    // '#' parents are buttons whose aria-expanded opens their menu: a mouse opens it on hover (a click then keeps
    // it open), a tap or Enter/Space toggles it, and Escape, leaving it or pressing outside closes it.
    setupToggles() {
      this.toggles = this.querySelectorAll('.thb-full-menu--toggle');
      if (!this.toggles.length) {
        return;
      }
      const signal = this._listeners.signal;
      this.toggles.forEach((toggle) => {
        const item = toggle.parentElement;
        let pointer = '';
        toggle.addEventListener('pointerdown', (event) => {
          pointer = event.pointerType;
        }, { signal });
        toggle.addEventListener('click', () => {
          this.setExpanded(toggle, pointer === 'mouse' || toggle.getAttribute('aria-expanded') !== 'true');
          pointer = '';
        }, { signal });
        item.addEventListener('pointerenter', (event) => {
          if (event.pointerType === 'mouse') this.setExpanded(toggle, true);
        }, { signal });
        item.addEventListener('pointerleave', (event) => {
          if (event.pointerType === 'mouse') this.setExpanded(toggle, false);
        }, { signal });
        item.addEventListener('focusout', (event) => {
          if (!item.contains(event.relatedTarget)) this.setExpanded(toggle, false);
        }, { signal });
        item.addEventListener('keydown', (event) => {
          if (event.key === 'Escape' && toggle.getAttribute('aria-expanded') === 'true') {
            this.setExpanded(toggle, false);
            toggle.focus();
          }
        }, { signal });
      });
      document.addEventListener('pointerdown', (event) => {
        this.toggles.forEach((toggle) => {
          if (!toggle.parentElement.contains(event.target)) this.setExpanded(toggle, false);
        });
      }, { signal });
    }
    setExpanded(toggle, open) {
      if (open) {
        this.toggles.forEach((other) => {
          if (other !== toggle) other.setAttribute('aria-expanded', 'false');
        });
      }
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    }
    resizeSubMenus() {
      this.submenus.forEach((submenu) => {
        let sub_submenus = submenu.querySelectorAll(':scope >.menu-item-has-children>.sub-menu');

        sub_submenus.forEach((sub_submenu) => {
          let w = sub_submenu.offsetWidth,
            l = sub_submenu.parentElement.parentElement.getBoundingClientRect().left + sub_submenu.parentElement.parentElement.clientWidth + 10,
            total = w + l;
          if (total > document.body.clientWidth) {
            sub_submenu.parentElement.classList.add('left-submenu');
          } else if (sub_submenu.parentElement.classList.contains('left-submenu')) {
            sub_submenu.parentElement.classList.remove('left-submenu');
          }
        });
      });
    }
    disconnectedCallback() {
      this._listeners?.abort();
    }
  }
  customElements.define('full-menu', FullMenu);
}
